/**
 * RFP Pack v2.1 — Context Object, playbooks, artifacts, quality grades.
 *
 * Buy-side comprehensive pack generation. Thin intake. Fat document.
 * No supplier enters Sensei. No invented numbers: anything not in the
 * Context Object or a versioned category playbook renders as [TBD] and is
 * listed in the Gaps register.
 */

export type CategoryId =
  | "3pl_contract_logistics"
  | "same_day_courier"
  | "warehousing"
  | "freight_transport"
  | "tms_visibility"
  | "wms_inventory"
  | "mro_indirect"
  | "packaging"
  | "contract_manufacturing"
  | "other";

export interface CategoryOption {
  id: CategoryId;
  label: string;
  pinned: boolean;
}

/** Free tier can generate this many RFP packs before being asked to upgrade. */
export const FREE_PACK_LIMIT = 1;

export const CATEGORY_OPTIONS: CategoryOption[] = [
  { id: "3pl_contract_logistics", label: "3PL / contract logistics", pinned: true },
  { id: "same_day_courier", label: "Same-day courier / radiopharmaceutical", pinned: true },
  { id: "warehousing", label: "Warehousing / fulfilment", pinned: true },
  { id: "freight_transport", label: "Freight / transportation", pinned: true },
  { id: "tms_visibility", label: "TMS / visibility platform", pinned: true },
  { id: "wms_inventory", label: "WMS / inventory system", pinned: true },
  { id: "mro_indirect", label: "MRO / indirect materials", pinned: true },
  { id: "packaging", label: "Packaging", pinned: true },
  { id: "contract_manufacturing", label: "Contract manufacturing / co-packer", pinned: true },
  { id: "other", label: "Other / custom category…", pinned: false },
];

// ─── Context Object (v1 schema, spec §3.1) ───────────────────────────────────

export interface ContextMeta {
  pack_id: string;
  category_id: CategoryId;
  /** Buyer-supplied name for the "Other / custom" category (e.g. "Digital
   *  Marketing", "IT Managed Services"). Overrides the playbook label and
   *  drives the services (non-supply-chain) domain guardrails. */
  custom_category_name?: string;
  format: "RFP";
  client_name: string;
  buyer_entity: string;
  geography: string[];
  currency: string;
  language: string;
  confidentiality: "standard" | "confidential" | "restricted";
}

export interface ContextProblem {
  current_state: string;
  current_state_constraints: string;
  business_problem: string;
  desired_outcome: string;
  success_metrics: string[];
  strategic_importance: string;
  incumbent: { exists: boolean | null; notes: string; end_date: string };
}

export interface ContextScope {
  in_scope: string[];
  out_of_scope: string[];
  sites: string[];
  volumes: string;
  systems: string[];
  services: string[];
  exclusions: string[];
}

export interface ContextConstraints {
  budget: { known: boolean; band: string; publish_to_bidders: boolean };
  go_live: string;
  contract_term_months: number | null;
  /** Extension options, e.g. "2 × 12-month extension options". */
  contract_extensions?: string;
  must_keep: string[];
  cannot_change: string[];
  regulatory: string[];
  headcount_rules: string;
}

export interface ContextStakeholders {
  sponsor: string;
  procurement_lead: string;
  technical_lead: string;
  ops_lead: string;
  legal: string;
  raci_notes: string;
  /** Issuer contact email bound into Section 3 ("single point of contact"). */
  contact_email: string;
}

export interface ContextCommercial {
  pricing_model_preference: string[];
  tco_years: number;
  payment_terms: string;
  price_validity_days: number;
  should_cost_notes: string;
  /** Prices quoted exclusive of taxes (e.g. VAT/GST) unless stated otherwise. */
  tax_exclusive: boolean;
  /** Fixed-price period in months (12 per RFP general conditions). */
  price_fix_months: number;
  /** Whether the buyer guarantees any volume of business. */
  volume_guaranteed: boolean;
  /** How the offer is priced: fixed rates for the fix period, or cost-plus mark-up. */
  pricing_basis: "fixed_term" | "cost_plus";
  /**
   * Financial / fee / penalty / surcharge structures from the briefing that
   * bidders must price into the commercial proposal (Section 7). Anything the
   * Success-Measures classifier routes as COMMERCIAL lands here — it never
   * appears in Section 1.
   */
  pricing_rules: string[];
}

/** Weighting per scorecard dimension. Extensible — users can add / remove
 *  dimensions during the weights review step. Always renders to 100%. */
export interface EvaluationWeights {
  [dim: string]: number;
}

/** Human labels for scorecard dimensions, keyed by the same dim ids
 *  (`technical_fit` → "Technical Fit"). Absent/blank entries fall back to a
 *  prettified dim id so Section 12 and the Scorecard.xlsx always resolve a
 *  clean category name — even for custom criteria the user adds. */
export type EvaluationWeightNames = Record<string, string>;

export interface ContextEvaluation {
  weights: EvaluationWeights;
  /** User-visible category names per dimension key (see EvaluationWeightNames). */
  weight_names: EvaluationWeightNames;
  mandatory_gates: string[];
  interviews: boolean;
  negotiation: boolean;
  supplier_count_target: string;
  /** How the agreement may be awarded (single exclusive vs split by site/service). */
  award_model: "exclusive" | "split_ok" | "multi_ok" | null;
  /** Explicit "weights reviewed & confirmed" flag surfaced as an intake step so
   *  a pack can never ship with unconsidered weighting. Defaults to false. */
  weights_reviewed: boolean;
}

export interface ContextProcess {
  issue_method: string;
  qa_method: string;
  submission_format: string[];
  page_limit_technical: number | null;
  deadline: string;
  /** Time of day submissions close (e.g. "17:00"), rendered with timezone. */
  submission_time: string;
  timezone: string;
  late_policy: "reject" | "grace";
  bidder_conference: boolean;
  /** Date the RFP is issued (cover + schedule of events). */
  issue_date: string;
  /** Date bidders' questions are due. */
  qa_deadline: string;
  /** Date consolidated Q&A responses are released to all bidders. */
  qa_response_date: string;
  /** Date shortlisted supplier presentations are held. */
  presentation_date: string;
  /** Anticipated award decision date. */
  award_target_date: string;
  /** Hard copies accepted in addition to electronic submission (default off). */
  hard_copy: boolean;
}

export interface ContextLegalFlags {
  personal_data: "yes" | "no" | null;
  /**
   * Buyer-facing description of the personal data in scope. Resolved from the
   * source when GDPR / PII / DTC data constraints are stated (e.g. "DTC
   * Returns & Customer PII (GDPR Compliant)") so Section 9 never ships
   * "Personal data handled: [TBD]" for a data-processing engagement.
   */
  personal_data_label: string;
  gdp: boolean;
  hazmat: boolean;
  food: boolean;
  soc2_required: boolean;
  /** True when SOC 2 was auto-defaulted (source never stated it) — Section 9
   * renders "Required / subject to standard vendor assessment" vs plain
   * "Required" for an explicitly-stated requirement. */
  soc2_unstated: boolean;
  attach_msa: boolean;
  /** Section 11 MSA Tier 1 / Tier 2 redlining rule (default on). */
  msa_redline_tiers: boolean;
  /**
   * Briefing-context liability / insurance statements routed from Section 1 by
   * the Success-Measures classifier (e.g. "Liability capped at $100/dose",
   * "No distinction between handling classes"). Rendered in Section 11 under a
   * labelled sub-heading so bidders must price/confirm the coverage
   * accordingly — never surfaced in Section 1.
   */
  liability_notes: string[];
}

export interface RFPContext {
  meta: ContextMeta;
  problem: ContextProblem;
  scope: ContextScope;
  constraints: ContextConstraints;
  stakeholders: ContextStakeholders;
  commercial: ContextCommercial;
  evaluation: ContextEvaluation;
  process: ContextProcess;
  legal_flags: ContextLegalFlags;
  source_files: string[];
  /**
   * Structured clarification questions extracted from internal strategy notes
   * ("Ask: …", "Question 3: …"). Routed into Appendix L (Vendor Questionnaire)
   * only — never into Section 1 or the main body.
   */
  vendor_questions: string[];
}

// ─── Category playbook ────────────────────────────────────────────────────────

export interface ComplianceMatrixSeed {
  id: string;
  category: string;
  requirement: string;
  mandatory: boolean;
  evidence: string;
  maps_to: string; // scorecard dimension key
  scope_tags: string[];
}

export interface PricingTemplateSeed {
  sheet: string;
  sections: Array<{ name: string; rows: string[] }>;
}

export interface SlaSeed {
  metric: string;
  target: string;
  evidence: string;
  mandatory: boolean;
}

export interface RubricAnchor {
  dimension: string;
  score: number;
  anchor: string;
}

export interface QuestionnaireQuestion {
  id: string;
  question: string;
  hint?: string;
}

export interface QuestionnaireSection {
  name: string;
  questions: QuestionnaireQuestion[];
}

export interface CategoryPlaybook {
  id: CategoryId;
  label: string;
  description: string;
  scope_seeds: { in_scope: string[]; out_of_scope: string[] };
  scope_tags: string[];
  matrix: ComplianceMatrixSeed[];
  pricing_template: PricingTemplateSeed;
  sla_catalogue: SlaSeed[];
  rubric_anchors: RubricAnchor[];
  legal_flags_hints: string[];
  insurance_minimums: string[];
  /** Optional vendor questionnaire seed (Appendix L / Vendor_Questionnaire.xlsx). */
  vendor_questionnaire?: QuestionnaireSection[];
  /** Optional SLA definition questionnaire seed (Appendix M / SLA_Questionnaire.xlsx). */
  sla_questionnaire?: QuestionnaireSection[];
  known?: boolean;
}

// ─── Pack artifacts / sections ────────────────────────────────────────────────

export type SectionId =
  | "s00_cover"
  | "s01_purpose"
  | "s02_scope"
  | "s03_response"
  | "s04_qualifications"
  | "s05_matrix"
  | "s06_solution"
  | "s07_commercial"
  | "s08_sla"
  | "s09_security"
  | "s10_esg"
  | "s11_contract"
  | "s12_evaluation"
  | "s13_appendices";

export const SECTION_IDS: SectionId[] = [
  "s00_cover",
  "s01_purpose",
  "s02_scope",
  "s03_response",
  "s04_qualifications",
  "s05_matrix",
  "s06_solution",
  "s07_commercial",
  "s08_sla",
  "s09_security",
  "s10_esg",
  "s11_contract",
  "s12_evaluation",
  "s13_appendices",
];

export interface PackSection {
  id: SectionId;
  num: string;
  title: string;
  body: string; // markdown-ish, rendered as HTML
  citations: string[];
  tbd_count: number;
}

/** Manual section content written by the section editor. */
export interface ManualSection {
  title: string;
  body: string;
}

export type ManualSections = Partial<Record<SectionId, ManualSection>>;

// ─── Matrix row (first-class artifact) ────────────────────────────────────────

export interface ComplianceMatrixRow {
  id: string;
  category: string;
  requirement: string;
  mandatory: boolean;
  supplier_response: "comply" | "partial" | "no" | "tbd";
  evidence: string;
  maps_to: string;
}

export interface ScorecardRow {
  dimension: string;
  weight: number;
  how_scored: string;
  /** User-configured category name for the dimension (weight_names override
   *  with a prettified default fallback). Rendered in Section 12 and the
   *  Scorecard.xlsx instead of the raw dimension key. */
  label: string;
}

export interface GapItem {
  key: string;
  label: string;
  severity: "red" | "amber";
  status: "open" | "waived" | "resolved";
}

export interface QualityCheck {
  id: string;
  severity: "pass" | "warn" | "block";
  label: string;
  detail: string;
}

export interface QualityReport {
  checks: QualityCheck[];
  pass_count: number;
  warn_count: number;
  block_count: number;
}

export type PackGrade = "draft" | "issue_ready";

export interface PackArtifacts {
  compliance_matrix: ComplianceMatrixRow[];
  scorecard: ScorecardRow[];
  rubric_anchors: RubricAnchor[];
  pricing_template: PricingTemplateSeed;
  gaps: GapItem[];
  /** Vendor questionnaire workbook seed (Appendix L). */
  vendor_questionnaire?: QuestionnaireSection[];
  /** SLA definition questionnaire workbook seed (Appendix M). */
  sla_questionnaire?: QuestionnaireSection[];
}

/**
 * Per-field provenance for the RAG unpack pass (Phase 3).
 *
 * Mirrors the backend's source-tagging so the UI can:
 *  - auto-accept values grounded in the draft (source === "draft")
 *  - flag inferred values for review (source === "ai_inferred") with a
 *    citation anchor (e.g. "draft §3 – Volumes")
 *  - explain why a field is still [TBD] (reason === "not stated in draft")
 *
 * Keyed by the RFPContext path, e.g. "constraints.go_live" or "scope.volumes".
 */
export type RAGSource = "draft" | "ai_inferred" | "playbook" | "user";

export interface RagFieldProvenance {
  source: RAGSource;
  reason: string;
  citation?: string;
}

export type RagProvenance = Record<string, RagFieldProvenance>;

export interface RFPack {
  id: string;
  pack_id: string;
  name: string;
  client_name: string;
  category_id: CategoryId;
  grade: PackGrade;
  created_at: string;
  updated_at: string;
  created_by: string;
  created_by_email: string;
  workspace: "consultant" | "company";
  context: RFPContext;
  sections: PackSection[];
  artifacts: PackArtifacts;
  quality: QualityReport;
  stale: boolean;
  waived_gaps: string[];
  /** Sections the user has deleted from the outline — hidden from canvas +
   *  exports and skipped by the generator on regeneration. */
  removed_sections?: SectionId[];
  /** Manual section overrides (title/body) written by the section editor —
   *  re-applied on regeneration so "Update pack" does not wipe edits. */
  manual_sections?: ManualSections;
  source_draft_text: string;
  source_draft_name: string;
  rag_source_status?: "none" | "indexing" | "unpacking" | "applied" | "error";
  rag_provenance?: RagProvenance;
}

/**
 * Viewer feedback on a shared RFP pack. Written by anyone who opens a shared
 * pack link and signs in; read back only by the pack owner.
 */
export interface RFPackFeedback {
  id: string;
  pack_id: string;
  created_by: string;
  created_by_email: string;
  comment: string;
  section_id?: string;
  section_title?: string;
  created_at: string;
}

export function emptyContext(category: CategoryId): RFPContext {
  return {
    meta: {
      pack_id: "",
      category_id: category,
      format: "RFP",
      client_name: "",
      buyer_entity: "",
      geography: [],
      currency: "GBP",
      language: "en-GB",
      confidentiality: "standard",
    },
    problem: {
      current_state: "",
      current_state_constraints: "",
      business_problem: "",
      desired_outcome: "",
      success_metrics: [],
      strategic_importance: "",
      incumbent: { exists: null, notes: "", end_date: "" },
    },
    scope: {
      in_scope: [],
      out_of_scope: [],
      sites: [],
      volumes: "",
      systems: [],
      services: [],
      exclusions: [],
    },
    constraints: {
      budget: { known: false, band: "", publish_to_bidders: false },
      go_live: "",
      contract_term_months: null,
      contract_extensions: "",
      must_keep: [],
      cannot_change: [],
      regulatory: [],
      headcount_rules: "",
    },
    stakeholders: {
      sponsor: "",
      procurement_lead: "",
      technical_lead: "",
      ops_lead: "",
      legal: "",
      raci_notes: "",
      contact_email: "",
    },
    commercial: {
      pricing_model_preference: [],
      tco_years: 3,
      // Client commercial terms are NEVER defaulted — they render as [TBD] and
      // are flagged in the gaps register until the buyer supplies them.
      payment_terms: "",
      price_validity_days: 0,
      should_cost_notes: "",
      tax_exclusive: true,
      price_fix_months: 12,
      volume_guaranteed: false,
      pricing_basis: "fixed_term",
      pricing_rules: [],
    },
    evaluation: {
      weights: { technical_fit: 35, approach: 20, commercial: 25, capability: 10, risk: 10 },
      weight_names: {},
      mandatory_gates: [],
      interviews: true,
      negotiation: true,
      supplier_count_target: "",
      award_model: null,
      weights_reviewed: false,
    },
    process: {
      issue_method: "email",
      qa_method: "email_consolidated",
      submission_format: [],
      page_limit_technical: null,
      deadline: "",
      submission_time: "",
      timezone: "Europe/London",
      late_policy: "reject",
      bidder_conference: false,
      issue_date: "",
      qa_deadline: "",
      qa_response_date: "",
      presentation_date: "",
      award_target_date: "",
      hard_copy: false,
    },
    legal_flags: {
      personal_data: null,
      personal_data_label: "",
      gdp: false,
      hazmat: false,
      food: false,
      soc2_required: false,
      soc2_unstated: true,
      attach_msa: true,
      msa_redline_tiers: true,
      liability_notes: [],
    },
    source_files: [],
    vendor_questions: [],
  };
}